import React, { useState, useEffect, useCallback, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter, Trash2, Edit, Calendar as CalendarIcon, Download, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AdvancedCalendar } from '@/components/ui/advanced-calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from '@/components/ui/use-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PaginationControls from '@/components/PaginationControls';

const ReceiptsTable = ({
  fetchReceipts,
  fetchReceiptsSummary,
  onDeleteReceipt,
  onDeleteReceiptsBatch,
  onDeleteAllReceipts,
  onEditReceipt,
  operators,
  flags,
  paymentMethods,
  sharedFilters,
  refreshTrigger
}) => {
  const { toast } = useToast();
  
  // -- State --
  const [receipts, setReceipts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  
  const [summary, setSummary] = useState({ totalValor: 0, count: 0 });
  const [loadingSummary, setLoadingSummary] = useState(false);

  // -- Shared Filters --
  const searchTerm = sharedFilters?.searchTerm || '';
  const setSearchTerm = sharedFilters?.updateSearchTerm || (() => {});
  const selectedOperators = sharedFilters?.selectedOperators || new Set();
  const setSelectedOperators = sharedFilters?.updateSelectedOperators || (() => {});
  const selectedFlags = sharedFilters?.selectedFlags || new Set();
  const setSelectedFlags = sharedFilters?.updateSelectedFlags || (() => {});
  const selectedPaymentMethods = sharedFilters?.selectedPaymentMethods || new Set();
  const setSelectedPaymentMethods = sharedFilters?.updateSelectedPaymentMethods || (() => {});
  const dateRange = sharedFilters?.dateRange || {startDate: null, endDate: null};
  const setDateRange = sharedFilters?.updateDateRange || (() => {});
  
  const [selectedReceipts, setSelectedReceipts] = useState(new Set());
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const operatorOptions = operators.map(op => ({ value: op.name, label: op.name }));
  const flagOptions = flags.map(f => ({ value: f.name, label: f.name }));
  const paymentMethodOptions = paymentMethods.map(pm => ({ value: pm.name, label: pm.name }));

  // -- Debounce --
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // -- Fetch Data --
  const loadReceipts = useCallback(async () => {
    // Se a função não estiver disponível, não fazer nada (evita o erro "not a function")
    if (!fetchReceipts) return;

    setLoading(true);
    const filters = {
        searchTerm: debouncedSearchTerm,
        dateRange,
        selectedOperators,
        selectedFlags,
        selectedPaymentMethods,
        isReceipts: true // Flag para o helper saber que é recebimentos
    };

    try {
      const { data, count } = await fetchReceipts({
          page: currentPage,
          pageSize,
          filters
      });

      setReceipts(data || []);
      setTotalCount(count || 0);
    } catch (error) {
       console.error("Erro ao carregar recebimentos:", error);
       toast({ title: "Erro", description: "Falha ao carregar recebimentos.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [fetchReceipts, currentPage, pageSize, debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods, refreshTrigger, toast]);

  const loadSummary = useCallback(async () => {
    if (!fetchReceiptsSummary) return;

    setLoadingSummary(true);
    const filters = {
        searchTerm: debouncedSearchTerm,
        dateRange,
        selectedOperators,
        selectedFlags,
        selectedPaymentMethods,
        isReceipts: true
    };

    try {
      const result = await fetchReceiptsSummary({ filters });
      setSummary(result);
    } catch (error) {
      console.error("Erro ao carregar resumo:", error);
    } finally {
      setLoadingSummary(false);
    }
  }, [fetchReceiptsSummary, debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods, refreshTrigger]);

  // -- Effects --
  useEffect(() => { loadReceipts(); }, [loadReceipts]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setCurrentPage(1); }, [debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods]);

  // -- Handlers --
  const handleSelectReceipt = receiptId => {
    setSelectedReceipts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) newSet.delete(receiptId);
      else newSet.add(receiptId);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedReceipts.size === receipts.length) setSelectedReceipts(new Set());
    else setSelectedReceipts(new Set(receipts.map(r => r.id)));
  };

  const handleDeleteBatch = async () => {
    await onDeleteReceiptsBatch(Array.from(selectedReceipts));
    setSelectedReceipts(new Set());
    loadReceipts();
    loadSummary();
  };

  const hasActiveFilters = Boolean(
    debouncedSearchTerm || 
    dateRange.startDate || 
    dateRange.endDate || 
    selectedOperators.size > 0 || 
    selectedFlags.size > 0 || 
    selectedPaymentMethods.size > 0
  );

  const handleDeleteFiltered = async () => {
    toast({ title: "Excluindo recebimentos...", description: "Isso pode levar alguns segundos dependendo da quantidade." });
    
    const filters = {
      searchTerm: debouncedSearchTerm,
      dateRange,
      selectedOperators,
      selectedFlags,
      selectedPaymentMethods,
      isReceipts: true
    };

    try {
      const { data } = await fetchReceipts({ page: 1, pageSize: 99999, filters });
      
      if (!data || data.length === 0) {
        toast({ title: "Sem dados", description: "Nenhum recebimento encontrado para excluir.", variant: "destructive" });
        return;
      }
      
      const idsToDelete = data.map(receipt => receipt.id);
      await onDeleteReceiptsBatch(idsToDelete);
      
      setSelectedReceipts(new Set());
      loadReceipts();
      loadSummary();
      toast({ title: "Sucesso!", description: `${idsToDelete.length} recebimentos foram excluídos.` });
    } catch (error) {
       console.error("Erro ao excluir recebimentos filtrados", error);
       toast({ title: "Erro", description: "Falha ao excluir os recebimentos.", variant: "destructive" });
    }
  };

  const handleExport = async () => {
    toast({ title: "Gerando PDF...", description: "Aguarde um momento." });
    
    const filters = {
        searchTerm: debouncedSearchTerm,
        dateRange,
        selectedOperators,
        selectedFlags,
        selectedPaymentMethods,
        isReceipts: true
    };

    const { data } = await fetchReceipts({ page: 1, pageSize: 2000, filters }); // Limite de 2000 para exportação

    if (!data || data.length === 0) {
        toast({ title: "Sem dados", description: "Nada para exportar.", variant: "destructive" });
        return;
    }

    const doc = new jsPDF('landscape');
    doc.text("Relatório de Recebimentos", 14, 16);

    const head = [["Data Pag.", "Operadora", "Bandeira", "Tipo", "Parcelas", "Valor", "Cód. Autorização", "Cód. Pedido"]];
    const body = data.map(receipt => [
        formatDate(receipt.data_pagamento),
        receipt.operators?.name || 'N/A',
        receipt.bandeira,
        receipt.tipo,
        receipt.parcelas || '-',
        formatCurrency(receipt.valor),
        receipt.codigo_autorizacao || '',
        receipt.codigopedido || ''
    ]);

    doc.autoTable({
        head: head,
        body: body,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save(`recebimentos_export_${new Date().toISOString().split('T')[0]}.pdf`);
    toast({ title: "Sucesso", description: "PDF gerado." });
  };

  const formatCurrency = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(value) || 0);
  const formatDate = dateString => {
    if (!dateString) return '-';
    return new Date(dateString.replace(/-/g, '/')).toLocaleDateString('pt-BR');
  };
  const resetFilters = () => {
    setDateRange({ startDate: null, endDate: null });
    setSearchTerm('');
    setSelectedOperators(new Set());
    setSelectedFlags(new Set());
    setSelectedPaymentMethods(new Set());
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-effect border-white/20">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-white/70 text-sm">Total de Recebimentos</p>
              <p className="text-2xl font-bold text-white">
                {loadingSummary ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : summary.count}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-effect border-white/20 md:col-span-3">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-white/70 text-sm">Valor Total Recebido</p>
              <p className="text-2xl font-bold text-blue-400">
                {loadingSummary ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : formatCurrency(summary.totalValor)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-effect border-white/20">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Filter className="w-5 h-5" />Filtros e Busca</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="relative md:col-span-2 lg:col-span-4">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white" />
            </div>
             <Popover>
                <PopoverTrigger asChild>
                    <Button variant={"outline"} className={cn("w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white hover:text-white hover:bg-white/20", !dateRange.startDate && "text-white/50")}><CalendarIcon className="mr-2 h-4 w-4" />{dateRange.startDate ? format(dateRange.startDate, "dd/MM/yyyy") : <span>Data inicial</span>}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><AdvancedCalendar mode="single" selected={dateRange.startDate} onSelect={date => setDateRange(prev => ({ ...prev, startDate: date }))} initialFocus /></PopoverContent>
            </Popover>
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant={"outline"} className={cn("w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white hover:text-white hover:bg-white/20", !dateRange.endDate && "text-white/50")}><CalendarIcon className="mr-2 h-4 w-4" />{dateRange.endDate ? format(dateRange.endDate, "dd/MM/yyyy") : <span>Data final</span>}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><AdvancedCalendar mode="single" selected={dateRange.endDate} onSelect={date => setDateRange(prev => ({ ...prev, endDate: date }))} initialFocus /></PopoverContent>
            </Popover>
            <MultiSelectFilter title="Operadoras" options={operatorOptions} selected={selectedOperators} onSelectedChange={setSelectedOperators} />
            <MultiSelectFilter title="Bandeiras" options={flagOptions} selected={selectedFlags} onSelectedChange={setSelectedFlags} />
            <MultiSelectFilter title="Formas Pagto" options={paymentMethodOptions} selected={selectedPaymentMethods} onSelectedChange={setSelectedPaymentMethods} />
            
            <Button onClick={resetFilters} variant="ghost" className="text-white/70 hover:text-white md:col-start-3">Limpar</Button>
            <Button onClick={handleExport} className="bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700"><Download className="w-4 h-4 mr-2" />PDF</Button>

          </div>
        </CardContent>
      </Card>

      <Card className="glass-effect border-white/20">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Recebimentos</CardTitle>
            {selectedReceipts.size > 0 && <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive"><Trash2 className="w-4 h-4 mr-2" /> Deletar ({selectedReceipts.size})</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Confirmar exclusão?</AlertDialogTitle><AlertDialogDescription>Excluir {selectedReceipts.size} recebimentos?</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDeleteBatch}>Deletar</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>}
          </div>
        </CardHeader>
        
        <PaginationControls
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / pageSize)}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          loading={loading}
          itemLabel="recebimentos"
          onDeleteAll={onDeleteAllReceipts}
          hasActiveFilters={hasActiveFilters}
          onDeleteFiltered={handleDeleteFiltered}
        />
        
        <CardContent>
          {loading ? (
             <div className="text-center py-12 text-white/70"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />Carregando...</div>
          ) : receipts.length === 0 ? <div className="text-center py-8 text-white/70">Nenhum recebimento encontrado.</div> : <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="p-3"><Checkbox checked={receipts.length > 0 && selectedReceipts.size === receipts.length} onCheckedChange={handleSelectAll} /></th>
                    <th className="text-left p-3 text-white/80">Data Pag.</th>
                    <th className="text-left p-3 text-white/80">Operadora</th>
                    <th className="text-left p-3 text-white/80">Bandeira</th>
                    <th className="text-left p-3 text-white/80">Tipo</th>
                    <th className="text-left p-3 text-white/80">Parcelas</th>
                    <th className="text-left p-3 text-white/80">Valor</th>
                    <th className="text-left p-3 text-white/80">Cód. Auto</th>
                    <th className="text-left p-3 text-white/80">Cód. Pedido</th>
                    <th className="text-left p-3 text-white/80">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => <tr key={receipt.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                      <td className="p-3"><Checkbox checked={selectedReceipts.has(receipt.id)} onCheckedChange={() => handleSelectReceipt(receipt.id)} /></td>
                      <td className="p-3 text-white text-sm">{formatDate(receipt.data_pagamento)}</td>
                      <td className="p-3 text-white text-sm">{receipt.operators?.name || 'N/A'}</td>
                      <td className="p-3 text-white text-sm">{receipt.bandeira}</td>
                      <td className="p-3 text-white text-sm capitalize">{receipt.tipo}</td>
                      <td className="p-3 text-white text-sm">{receipt.parcelas || '-'}</td>
                      <td className="p-3 text-white text-sm">{formatCurrency(receipt.valor)}</td>
                      <td className="p-3 text-white text-sm">{receipt.codigo_autorizacao}</td>
                      <td className="p-3 text-white text-sm">{receipt.codigopedido || ''}</td>
                      <td className="p-3 flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onEditReceipt(receipt)} className="text-blue-400 hover:text-blue-300"><Edit className="w-4 h-4" /></Button>
                        <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Excluir?</AlertDialogTitle></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Não</AlertDialogCancel><AlertDialogAction onClick={() => { onDeleteReceipt(receipt.id); loadReceipts(); loadSummary(); }}>Sim</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </CardContent>
      </Card>
      
      <PaginationControls
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / pageSize)}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        itemLabel="recebimentos"
        onDeleteAll={onDeleteAllReceipts}
        hasActiveFilters={hasActiveFilters}
        onDeleteFiltered={handleDeleteFiltered}
      />
    </div>;
}; // Removed memo

export default ReceiptsTable;
